import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { supabasePublic } from '@/integrations/supabase/publicClient';
import { TableBookablePeriod } from '@/types/table';
import { useToast } from '@/hooks/use-toast';

interface UseTableBookablePeriodsOptions {
  establishmentId?: string | null;
  useAuthenticated?: boolean;
}

export function useTableBookablePeriods(options: UseTableBookablePeriodsOptions = {}) {
  const { establishmentId, useAuthenticated = false } = options;
  const [bookablePeriods, setBookablePeriods] = useState<TableBookablePeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const fetchBookablePeriods = useCallback(async () => {
    if (!establishmentId) {
      setBookablePeriods([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const client = useAuthenticated ? supabase : supabasePublic;
      
      const { data, error: fetchError } = await client
        .from('table_bookable_periods')
        .select('*')
        .eq('establishment_id', establishmentId)
        .order('start_date', { ascending: true });

      if (fetchError) throw fetchError;

      const periods: TableBookablePeriod[] = (data || []).map((p: any) => ({
        id: p.id,
        tableId: p.table_id,
        establishmentId: p.establishment_id,
        startDate: new Date(p.start_date),
        endDate: new Date(p.end_date),
        reason: p.reason || undefined,
        createdAt: new Date(p.created_at),
      }));

      setBookablePeriods(periods);
    } catch (err) {
      console.error('Error fetching bookable periods:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [establishmentId, useAuthenticated]);

  useEffect(() => {
    fetchBookablePeriods();
  }, [fetchBookablePeriods]);

  const addBookablePeriod = async (
    tableId: string,
    startDate: Date,
    endDate: Date,
    reason?: string
  ): Promise<boolean> => {
    if (!establishmentId) return false;

    try {
      const { error: insertError } = await supabase
        .from('table_bookable_periods')
        .insert({
          table_id: tableId,
          establishment_id: establishmentId,
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
          reason: reason || null,
        });

      if (insertError) throw insertError;

      toast({
        title: "✅ Période ajoutée",
        description: "La table sera hors réservation automatique pour cette période.",
      });

      await fetchBookablePeriods();
      return true;
    } catch (err) {
      console.error('Error adding bookable period:', err);
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter la période.",
        variant: "destructive",
      });
      return false;
    }
  };

  const removeBookablePeriod = async (periodId: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('table_bookable_periods')
        .delete()
        .eq('id', periodId);

      if (deleteError) throw deleteError;

      toast({
        title: "✅ Période supprimée",
        description: "La table est de nouveau réservable pour cette période.",
      });

      await fetchBookablePeriods();
      return true;
    } catch (err) {
      console.error('Error removing bookable period:', err);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la période.",
        variant: "destructive",
      });
      return false;
    }
  };

  const getPeriodsForTable = (tableId: string): TableBookablePeriod[] => {
    return bookablePeriods.filter(p => p.tableId === tableId);
  };

  return {
    bookablePeriods,
    loading,
    error,
    addBookablePeriod,
    removeBookablePeriod,
    getPeriodsForTable,
    refetch: fetchBookablePeriods,
  };
}
